import LegalPageLayout, { type LegalSection } from "@/components/feature/LegalPageLayout";

const sections: LegalSection[] = [
  {
    heading: "Introduction",
    paragraphs: [
      "Rythugaru Agri Tourism respects your privacy and is committed to protecting the personal information you share with us. This Privacy Policy explains what we collect, why we collect it, and how we keep it safe.",
      "It applies to our website and to every enquiry, booking and farm experience arranged through Rythugaru. By using our website, you consent to the practices described in this policy.",
    ],
  },
  {
    heading: "Information We Collect",
    paragraphs: [
      "We only collect information that helps us respond to you and arrange your visit. You may share some of this directly, and some may be collected automatically as you browse.",
    ],
    points: [
      "Contact details you submit, such as your name, email address and phone number.",
      "Booking details, including your preferred dates, group size and any special requests.",
      "Messages and enquiries you send through our contact and partner forms.",
      "Basic technical data such as browser type, device and pages visited.",
    ],
  },
  {
    heading: "How We Use Your Information",
    paragraphs: [
      "Your information is used to communicate with you, confirm and coordinate your experience, and improve how Rythugaru works for both guests and farmers.",
      "We may also use your email to send occasional updates about new experiences or seasonal offerings, but only where you have chosen to receive them, and you can unsubscribe at any time.",
    ],
  },
  {
    heading: "Cookies and Analytics",
    paragraphs: [
      "Our website may use cookies and similar technologies to remember your preferences and understand how visitors use our pages. This helps us improve navigation, content and performance.",
      "You can control or disable cookies through your browser settings. Some parts of the website may not work as smoothly if cookies are turned off.",
    ],
  },
  {
    heading: "Sharing Your Information",
    paragraphs: [
      "To arrange an experience we may share the details necessary to host you with the relevant farm family, such as your name, group size and arrival date.",
      "We do not sell your personal information. We only share it with trusted partners who help us operate our services, and only to the extent needed, or where required by law.",
    ],
  },
  {
    heading: "Data Retention",
    paragraphs: [
      "We keep your information only for as long as necessary to fulfil the purposes described in this policy, to comply with legal obligations, and to resolve any disputes.",
      "When information is no longer needed, we securely delete or anonymise it.",
    ],
  },
  {
    heading: "Your Rights",
    paragraphs: [
      "You have control over the personal information you share with us. You can ask us to access, correct, update or delete your data at any time.",
      "To exercise any of these rights, simply contact us using the details below and we will respond as quickly as we can.",
    ],
  },
  {
    heading: "Data Security",
    paragraphs: [
      "We use reasonable technical and organisational measures to protect your information from unauthorised access, loss or misuse. Data is transmitted over secure connections and access is limited to those who need it.",
      "While we work hard to protect your data, no method of transmission over the internet is completely secure, and we cannot guarantee absolute security.",
    ],
  },
  {
    heading: "Children's Privacy",
    paragraphs: [
      "Many of our experiences are designed for families and children, but bookings must be made by a parent or guardian. We do not knowingly collect personal information directly from children.",
      "If you believe a child has provided us with personal information without consent, please contact us and we will delete it.",
    ],
  },
  {
    heading: "Third-Party Links",
    paragraphs: [
      "Our website may contain links to external services, such as maps, social media pages or payment providers. We are not responsible for the privacy practices or content of those third-party sites.",
      "We encourage you to review the privacy policies of any external site you visit through links on our website.",
    ],
  },
  {
    heading: "Changes to This Policy",
    paragraphs: [
      "We may update this Privacy Policy from time to time to reflect improvements in our services or changes in the law. Any updates will be published on this page with a new date.",
      "We encourage you to review this page occasionally to stay informed about how we protect your information.",
    ],
  },
  {
    heading: "Contact Us",
    paragraphs: [
      "If you have any questions, concerns or requests about your privacy or this policy, please reach out to the Rythugaru team.",
      "You can reach us through the contact details in the footer of this website or via our contact page, and we will be glad to help.",
    ],
  },
];

export default function Privacy() {
  return (
    <LegalPageLayout
      eyebrow="Legal"
      title="Privacy Policy"
      intro="Your trust matters to us. This policy explains what personal information Rythugaru collects, how it is used, and the choices you have over your data when you browse our website or book a farm experience."
      updated="17 September 2026"
      path="/privacy"
      seoTitle="Privacy Policy | Rythugaru Agri Tourism"
      seoDescription="Learn how Rythugaru Agri Tourism collects, uses and protects your personal data, including your rights when you book a farm experience."
      seoImage="https://readdy.ai/api/search-image?query=Quiet%20dawn%20over%20a%20rural%20Indian%20village%20with%20a%20closed%20wooden%20gate%20and%20soft%20mist%20over%20green%20fields%2C%20sense%20of%20safety%20and%20privacy%2C%20gentle%20blue%20and%20green%20tones%20with%20warm%20sunrise%20edge%20light%2C%20cinematic%20wide%20landscape%2C%20ultra%20detailed&width=1200&height=630&seq=privacy-og&orientation=landscape"
      sections={sections}
    />
  );
}